import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { cart } = useCart();
  const { user, logout, hasAnyRole } = useAuth();
  const { t } = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const isAdmin = hasAnyRole(['admin', 'developer']);

  const navLinks = [
    { to: '/', icon: '🏠', label: t('nav.home') },
    { to: '/shop', icon: '🛍️', label: t('nav.shop') },
    // Cible du QR code imprimé au dos de la boîte : un acheteur qui scanne doit
    // aussi pouvoir la retrouver depuis le menu, pas seulement le pied de page.
    { to: '/regles-de-jeu', icon: '📖', label: t('nav.rules') },
    { to: '/about', icon: 'ℹ️', label: t('nav.about') },
    { to: '/contact', icon: '💬', label: t('nav.contact') },
  ];

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const handleCloseMenu = () => setIsOpen(false);

  return (
    <nav className="sticky top-0 z-[100] bg-white shadow-lg border-b-2 border-primary">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-5 h-[60px] sm:h-[70px] flex items-center justify-between gap-2">

        {/* Logo */}
        <Link
          to="/"
          onClick={handleCloseMenu}
          className="flex items-center gap-1.5 sm:gap-2.5 text-primary font-bold transition-transform duration-300 hover:scale-105 whitespace-nowrap flex-shrink-0"
        >
          <img
            src="/logo-200.webp"
            alt="Mirum Games Logo"
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </Link>

        {/* Langue + menu, groupes a droite.
            La langue est VISIBLE sans ouvrir le burger : jusqu'ici le selecteur
            n'existait qu'a l'interieur du menu replie (conteneur `hidden` tant
            que isOpen est faux) et dans le bloc `hidden md:flex` du desktop.
            Sous 768 px — c'est-a-dire quand on scanne le QR code imprime au dos
            de la boite — un visiteur neerlandophone n'avait AUCUN indice qu'une
            version dans sa langue existe.

            Le groupe est enveloppe dans un seul conteneur : le parent est en
            justify-between, y inserer un enfant isole deplacerait le burger. */}
        <div className="md:hidden ml-auto flex items-center gap-2">
        <LanguageSwitcher compact />
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          className="flex flex-col gap-1.5 p-2"
        >
          <span className={`w-7 h-0.5 bg-blue-600 rounded-sm transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
          <span className={`w-7 h-0.5 bg-blue-600 rounded-sm transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
          <span className={`w-7 h-0.5 bg-blue-600 rounded-sm transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
        </button>
        </div>

        {/* Menu Links */}
        <div className={`
          md:flex md:gap-2 md:items-center
          ${isOpen ? 'flex' : 'hidden'}
          md:static md:flex-row md:w-auto md:p-0 md:shadow-none md:border-0
          fixed top-[60px] sm:top-[70px] inset-x-0 bottom-0 flex-col bg-white p-3 gap-2 shadow-xl border-t-2 border-primary z-50
          overflow-y-auto overflow-x-hidden box-border w-screen max-w-[100vw] md:overflow-visible
        `}>
          {/* Navigation principale */}
          <div className="md:flex md:flex-row md:gap-2 md:items-center flex flex-col gap-1 md:gap-2 w-full md:w-auto box-border">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={handleCloseMenu}
                className="flex items-center gap-3 px-3 py-3 md:py-2 md:gap-2 text-text rounded-lg transition-all duration-300 font-semibold md:font-medium text-sm md:text-sm relative group hover:bg-blue-50 hover:text-blue-600 active:scale-95 box-border"
              >
                <span className="text-lg md:text-lg flex-shrink-0">{link.icon}</span>
                <span className="md:hidden lg:inline truncate">{link.label}</span>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 rounded-sm transition-all duration-300 group-hover:w-full"></span>
              </Link>
            ))}
          </div>

          {/* Actions mobile */}
          <div className="md:hidden flex flex-col gap-2 mt-2 pt-3 border-t-2 border-gray-100 w-full box-border">
            {/* Panier */}
            <Link
              to="/cart"
              onClick={handleCloseMenu}
              className="flex items-center justify-between px-3 py-3 text-text rounded-lg bg-gradient-to-r from-blue-50 to-blue-100/50 hover:from-blue-100 hover:to-blue-200/50 transition-all active:scale-95 shadow-sm box-border"
            >
              <span className="flex items-center gap-2 font-semibold truncate">
                <span className="text-xl flex-shrink-0">🛒</span>
                <span className="text-sm truncate">{t('nav.cart')}</span>
              </span>
              {cart.length > 0 && (
                <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md flex-shrink-0">
                  {cart.length}
                </span>
              )}
            </Link>

            {/* Admin */}
            {user && isAdmin && (
              <Link
                to="/admin"
                onClick={handleCloseMenu}
                className="flex items-center gap-2 px-3 py-3 text-white bg-gradient-to-r from-blue-800 to-blue-900 rounded-lg font-bold hover:from-blue-900 hover:to-black transition-all active:scale-95 shadow-md box-border"
              >
                <span className="text-xl flex-shrink-0">⚙️</span>
                <span className="text-sm truncate">{t('nav.admin')}</span>
              </Link>
            )}

            {/* Commander - CTA principal */}
            <Link
              to={cart.length > 0 ? "/checkout" : "/cart"}
              onClick={handleCloseMenu}
              className="px-3 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg text-center hover:from-blue-700 hover:to-blue-800 transition-all active:scale-95 shadow-lg text-sm box-border"
            >
              <span className="flex items-center justify-center gap-2 truncate">
                <span className="text-lg flex-shrink-0">🎯</span>
                <span className="truncate">{t('nav.order')}</span>
              </span>
            </Link>

            {/* Choix de langue, aussi accessible depuis le menu mobile */}
            <div className="flex justify-center py-1">
              <LanguageSwitcher />
            </div>

            {/* User Section or Login */}
            {user ? (
              <div className="flex flex-col gap-2 mt-2 w-full box-border">
                <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2 w-full box-border">
                  <span className="text-xl flex-shrink-0">👤</span>
                  <div className="overflow-hidden min-w-0 flex-1">
                    <div className="font-bold text-text text-sm truncate">{user.fullName}</div>
                    <div className="text-[10px] text-gray-500 truncate">{user.email}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 w-full box-border">
                  <Link
                    to="/profile/orders"
                    onClick={handleCloseMenu}
                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 text-text rounded-lg bg-white border-2 border-gray-100 hover:border-blue-200 transition-all active:scale-95 w-full box-border"
                  >
                    <span className="text-xl">📦</span>
                    <span className="text-[10px] font-bold truncate w-full text-center">{t('nav.orders')}</span>
                  </Link>

                  <Link
                    to="/profile"
                    onClick={handleCloseMenu}
                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 text-text rounded-lg bg-white border-2 border-gray-100 hover:border-blue-200 transition-all active:scale-95 w-full box-border"
                  >
                    <span className="text-xl">⚙️</span>
                    <span className="text-[10px] font-bold truncate w-full text-center">{t('nav.profile')}</span>
                  </Link>
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full px-3 py-3 text-red-500 bg-red-50 border-2 border-red-100 rounded-lg font-bold hover:bg-red-100 transition-all active:scale-95 flex items-center justify-center gap-2 text-sm box-border"
                >
                  <span className="text-lg flex-shrink-0">🚪</span>
                  <span className="truncate">{t('nav.logout')}</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={handleCloseMenu}
                className="px-3 py-3 bg-white text-blue-600 border-2 border-blue-600 font-bold rounded-lg text-center hover:bg-blue-600 hover:text-white transition-all active:scale-95 text-sm w-full box-border"
              >
                <span className="flex items-center justify-center gap-2 truncate">
                  <span className="text-lg flex-shrink-0">🔑</span>
                  <span className="truncate">{t('nav.login')}</span>
                </span>
              </Link>
            )}
          </div>
        </div>

        {/* Right Section - Desktop */}
        <div className="hidden md:flex items-center gap-3 ml-auto">

          {/* Langue */}
          <LanguageSwitcher />

          {/* Cart */}
          <Link
            to="/cart"
            onClick={() => setIsOpen(false)}
            className="relative flex items-center justify-center w-11 h-11 text-primary rounded-lg transition-all duration-300 hover:bg-primary/10"
          >
            <span className="text-2xl">🛒</span>
            {/* Blanc et non noir : sur le vert #1F5A51, le noir tombe à
                2,64:1 quand la norme d'accessibilité est 4,5:1. Le blanc monte
                à 7,9:1. Même correctif que le bouton « Commander » plus bas. */}
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center animate-pulse-slow">
                {cart.length}
              </span>
            )}
          </Link>

          {/* Admin Link */}
          {user && isAdmin && (
            <Link
              to="/admin"
              onClick={() => setIsOpen(false)}
              title={t('nav.adminTitle')}
              className="flex items-center gap-1.5 px-3.5 py-2 text-black bg-white border-1 border-primary rounded-lg font-semibold text-[13px] transition-all duration-300 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-glow whitespace-nowrap"
            >
              <span className="text-lg">⚙️</span>
              <span className="hidden lg:inline">{t('nav.adminShort')}</span>
            </Link>
          )}

          {/* Checkout Button */}
          <Link
            to={cart.length > 0 ? "/checkout" : "/cart"}
            onClick={() => setIsOpen(false)}
            title={cart.length === 0 ? t('nav.cartEmpty') : t('nav.checkout')}
          >
            <button className="px-4 py-2.5 bg-primary text-white font-bold text-[13px] rounded-lg shadow-md transition-all duration-300 hover:bg-primary-light hover:-translate-y-0.5 hover:shadow-glow active:translate-y-0 whitespace-nowrap">
              🎯 {t('nav.order')}
            </button>
          </Link>

          {/* User Menu */}
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-2 bg-primary/15 border-2 border-primary rounded-lg text-text font-semibold text-[13px] transition-all duration-300 hover:bg-primary/25 hover:border-primary-light hover:-translate-y-0.5 whitespace-nowrap"
              >
                <span className="text-base">👤</span>
                <span className="hidden lg:inline">{user.fullName}</span>
                <span className={`text-[10px] text-primary transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`}>▼</span>
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div className="absolute top-[calc(100%+8px)] right-0 bg-white rounded-xl shadow-2xl ring-1 ring-gray-900/10 min-w-[260px] overflow-hidden animate-in slide-in-from-top-2 duration-300 z-50">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-gray-200">
                    <div className="font-bold text-base text-gray-900">{user.fullName}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{user.email}</div>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/profile/orders"
                      onClick={() => {
                        setShowUserMenu(false);
                        setIsOpen(false);
                      }}
                      className="w-full px-4 py-3 text-gray-700 text-sm font-medium flex items-center gap-3 transition-all duration-200 hover:bg-gray-50 hover:text-blue-600"
                    >
                      <span className="text-lg">📦</span>
                      <span>{t('nav.myOrders')}</span>
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => {
                        setShowUserMenu(false);
                        setIsOpen(false);
                      }}
                      className="w-full px-4 py-3 text-gray-700 text-sm font-medium flex items-center gap-3 transition-all duration-200 hover:bg-gray-50 hover:text-blue-600"
                    >
                      <span className="text-lg">👤</span>
                      <span>{t('nav.myProfile')}</span>
                    </Link>
                  </div>
                  <div className="border-t border-gray-200">
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-3 text-red-600 text-sm font-medium flex items-center gap-3 transition-all duration-200 hover:bg-red-50"
                    >
                      <span className="text-lg">🚪</span>
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" onClick={() => setIsOpen(false)}>
              <button className="px-4 py-2.5 bg-gray-100 text-text border-2 border-primary font-bold text-[13px] rounded-lg transition-all duration-300 hover:bg-primary hover:text-white hover:-translate-y-0.5 whitespace-nowrap">
                🔑 {t('nav.loginShort')}
              </button>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
